import { useEffect, useMemo, useRef, useState } from 'preact/hooks';
import { animals, schedules, recentLogs, openTimers, nowTick } from '@/state/data';
import { computeNextDue, timeShort } from '@/lib/due';
import { openSheet, closeSheet, showToast } from '@/state/ui';
import { logFeeding, undoFeeding } from '@/lib/mutations';
import { SnoozeSheet } from '@/components/SnoozeSheet';
import type { BoardEntry } from '@/state/board';
import type { Animal, Schedule } from '@/types';

const DAY_START_H = 6;
const DAY_END_H = 22;
const ROW_PX = 64;

interface CalEvent {
  id: string;
  kind: 'done' | 'overdue' | 'sched' | 'timer';
  at: Date;
  span?: { until: Date }; // continuous block (fast intervals)
  title: string;
  sub?: string;
  animal?: Animal;
  schedule?: Schedule;
}

function yFor(d: Date): number {
  const mins = (d.getHours() - DAY_START_H) * 60 + d.getMinutes();
  return (mins / 60) * ROW_PX;
}

function buildEvents(): CalEvent[] {
  const out: CalEvent[] = [];
  const now = new Date();
  const dayStart = new Date(now);
  dayStart.setHours(0, 0, 0, 0);
  const dayEnd = new Date(dayStart.getTime() + 86400000);

  // 1. Today's completed feedings (green)
  for (const log of recentLogs.value) {
    const at = new Date(log.fed_at);
    if (at < dayStart || at >= dayEnd || log.skipped) continue;
    const a = animals.value.get(log.animal_id);
    if (!a) continue;
    out.push({
      id: `log-${log.id}`,
      kind: 'done',
      at,
      title: `${a.name} ${log.weight_g != null && log.amount == null ? 'weighed' : 'fed'}`,
      sub: timeShort(at),
      animal: a,
    });
  }

  // 2. Upcoming + overdue from schedules
  for (const s of schedules.value.values()) {
    if (!s.enabled) continue;
    const a = animals.value.get(s.animal_id);
    if (!a || a.status !== 'active') continue;
    const next = computeNextDue(s, now);
    if (!next || next >= dayEnd) continue;

    const label = a.kind === 'batch' ? `${a.name} ×${a.count}` : a.name;
    const amount = [s.amount != null ? `${s.amount} ${s.unit ?? ''}`.trim() : null, s.food]
      .filter(Boolean)
      .join(' ');

    if (next < now) {
      out.push({
        id: `od-${s.id}`,
        kind: 'overdue',
        at: next,
        title: `${label} · overdue`,
        sub: `was ${timeShort(next)}`,
        animal: a,
        schedule: s,
      });
      continue;
    }

    if (s.stype === 'interval' && (s.interval_minutes ?? 60) < 60) {
      // fast cycle -> one continuous block to the end of its window/day
      const endMin = s.active_end ? s.active_end.slice(0, 5) : null;
      const until = new Date(next);
      if (endMin) {
        const [h, m] = endMin.split(':').map(Number);
        until.setHours(h, m, 0, 0);
      } else {
        until.setHours(DAY_END_H, 0, 0, 0);
      }
      out.push({
        id: `span-${s.id}`,
        kind: 'sched',
        at: next,
        span: { until: until > next ? until : new Date(next.getTime() + 3600000) },
        title: `${label} · every ${s.interval_minutes} min`,
        sub: amount || undefined,
        animal: a,
        schedule: s,
      });
    } else {
      // discrete occurrences for the rest of today (max 6 per schedule)
      let cursor = next;
      let guard = { ...s } as Schedule;
      for (let i = 0; i < 6 && cursor && cursor < dayEnd; i++) {
        out.push({
          id: `oc-${s.id}-${i}`,
          kind: 'sched',
          at: cursor,
          title: label,
          sub: amount || undefined,
          animal: a,
          schedule: s,
        });
        guard = { ...guard, last_fed_at: cursor.toISOString(), snoozed_until: null };
        const n = computeNextDue(guard, cursor);
        if (!n || n.getTime() === cursor.getTime()) break;
        cursor = n;
      }
    }
  }

  // 3. Ad-hoc timers
  for (const t of openTimers.value) {
    const at = new Date(t.due_at);
    if (at < dayStart || at >= dayEnd) continue;
    const a = t.animal_id ? animals.value.get(t.animal_id) : undefined;
    out.push({
      id: `tm-${t.id}`,
      kind: 'timer',
      at,
      title: a ? `${a.name} · ${t.label}` : t.label,
      sub: timeShort(at),
      animal: a ?? undefined,
    });
  }

  return out.sort((x, y) => x.at.getTime() - y.at.getTime());
}

interface Placed {
  ev: CalEvent;
  top: number;
  height: number;
  lane: number;
  lanes: number;
}

/** Greedy lane assignment so simultaneous events sit side-by-side
 *  instead of stacking. */
function layoutEvents(events: CalEvent[]): Placed[] {
  const boxes = events
    .map((ev) => {
      const top = Math.max(0, yFor(ev.at));
      const height = ev.span ? Math.max(34, yFor(ev.span.until) - top) : 38;
      return { ev, top, height, lane: 0, lanes: 1, bottom: top + height + 4 };
    })
    .filter((b) => b.top >= 0);

  // cluster overlapping boxes, assign lanes greedily
  let cluster: typeof boxes = [];
  let clusterEnd = -1;
  const flush = () => {
    const laneEnds: number[] = [];
    for (const b of cluster) {
      let lane = laneEnds.findIndex((end) => end <= b.top);
      if (lane === -1) {
        lane = laneEnds.length;
        laneEnds.push(0);
      }
      laneEnds[lane] = b.bottom;
      b.lane = lane;
    }
    for (const b of cluster) b.lanes = Math.min(laneEnds.length, 3);
    cluster = [];
  };
  for (const b of boxes) {
    if (cluster.length && b.top >= clusterEnd) flush();
    cluster.push(b);
    clusterEnd = Math.max(clusterEnd, b.bottom);
  }
  flush();
  // cap at 3 lanes; overflow shares the last lane
  for (const b of boxes) if (b.lane > 2) b.lane = 2;
  return boxes;
}

function EventSheet({ ev }: { ev: CalEvent }) {
  const a = ev.animal;
  const s = ev.schedule;
  const next = s ? computeNextDue(s) : null;
  const fed = async () => {
    if (!a) return;
    closeSheet();
    const logId = await logFeeding({ animalId: a.id, scheduleId: s?.id ?? null });
    showToast({ kind: 'undo', message: `${a.name} fed`, actionLabel: 'Undo', onAction: () => void undoFeeding(logId) }, 5000);
  };
  return (
    <div class="evt-sheet">
      <div class="evt-sheet__row"><span>What</span><span>{ev.title}</span></div>
      <div class="evt-sheet__row"><span>When</span><span>{timeShort(ev.at)}{ev.span ? ` – ${timeShort(ev.span.until)}` : ''}</span></div>
      {s?.last_fed_at && (
        <div class="evt-sheet__row"><span>Last fed</span><span>{timeShort(new Date(s.last_fed_at))}</span></div>
      )}
      {next && <div class="evt-sheet__row"><span>Next due</span><span>{timeShort(next)}</span></div>}
      {a?.diet_notes && <div class="evt-sheet__row"><span>Notes</span><span style={{ textAlign: 'right', maxWidth: '60%' }}>{a.diet_notes}</span></div>}
      {a && (ev.kind === 'sched' || ev.kind === 'overdue') && (
        <div class="evt-sheet__actions">
          {s && (
            <button
              class="btn btn--secondary"
              onClick={() => {
                closeSheet();
                setTimeout(() => {
                  const entry = { schedule: s, animal: a, nextDue: next, state: 'overdue' } as BoardEntry;
                  openSheet({ title: `Snooze ${a.name}`, content: <SnoozeSheet entry={entry} /> });
                }, 220);
              }}
            >
              Snooze
            </button>
          )}
          <button class="btn" onClick={fed}>Fed</button>
        </div>
      )}
    </div>
  );
}

export function CalendarScreen() {
  void nowTick.value;
  const [mode, setMode] = useState<'day' | 'agenda'>('day');
  const scrollRef = useRef<HTMLDivElement>(null);
  const events = useMemo(buildEvents, [nowTick.value, schedules.value, recentLogs.value, openTimers.value]);
  const now = new Date();
  const nowY = yFor(now);

  useEffect(() => {
    // park the now-line a third of the way down the viewport
    const el = scrollRef.current;
    if (el && mode === 'day') el.scrollTop = Math.max(0, nowY - el.clientHeight / 3);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode]);

  const hours: number[] = [];
  for (let h = DAY_START_H; h <= DAY_END_H; h++) hours.push(h);

  const open = (ev: CalEvent) => openSheet({ title: ev.animal?.name ?? 'Event', content: <EventSheet ev={ev} /> });

  const headDate = now.toLocaleDateString([], { month: 'long', day: 'numeric' });

  return (
    <div class="screen screen--fade-in">
      <div class="cal-head">
        <div>
          <p class="t-eyebrow">{headDate}</p>
          <h1 class="t-h1">Today</h1>
        </div>
        <button class="btn btn--secondary btn--sm" onClick={() => setMode(mode === 'day' ? 'agenda' : 'day')}>
          {mode === 'day' ? 'Day · Agenda' : 'Agenda · Day'}
        </button>
      </div>

      {mode === 'day' ? (
        <div class="cal-scroll" ref={scrollRef}>
          <div class="cal-grid" style={{ height: `${(DAY_END_H - DAY_START_H + 1) * ROW_PX}px` }}>
            {hours.map((h) => (
              <div key={h} class="cal-row" style={{ position: 'absolute', top: `${(h - DAY_START_H) * ROW_PX}px`, left: 0, right: 0 }}>
                <span class="cal-hour">
                  {h % 12 === 0 ? 12 : h % 12} {h < 12 ? 'AM' : 'PM'}
                </span>
              </div>
            ))}
            {layoutEvents(events).map(({ ev, top, height, lane, lanes }) => (
              <button
                key={ev.id}
                class={`cal-evt cal-evt--${ev.kind} ${ev.span ? 'cal-evt--span' : ''}`}
                style={{
                  top: `${top}px`,
                  height: `${height}px`,
                  left: lanes > 1 ? `calc(54px + ${lane} * ((100% - 70px) / ${lanes}))` : undefined,
                  right: lanes > 1 ? 'auto' : undefined,
                  width: lanes > 1 ? `calc((100% - 70px) / ${lanes})` : undefined,
                }}
                onClick={() => open(ev)}
              >
                <b>{ev.title}</b>
                {ev.sub ? <span class="cal-evt__sub"> · {ev.sub}</span> : null}
              </button>
            ))}
            {now.getHours() >= DAY_START_H && now.getHours() <= DAY_END_H && (
              <div class="cal-now-line" style={{ top: `${nowY}px` }} />
            )}
          </div>
        </div>
      ) : (
        <div class="cal-scroll">
          <div class="agenda">
            {(['Morning', 'Afternoon', 'Evening'] as const).map((part) => {
              const range = part === 'Morning' ? [0, 12] : part === 'Afternoon' ? [12, 17] : [17, 24];
              const items = events.filter((e) => e.at.getHours() >= range[0] && e.at.getHours() < range[1]);
              if (!items.length) return null;
              return (
                <div key={part}>
                  <p class="t-eyebrow" style={{ margin: '10px 4px 8px' }}>{part}</p>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                    {items.map((ev) => (
                      <button
                        key={ev.id}
                        class={`agenda-item ${ev.kind === 'done' ? 'agenda-item--done' : ''} ${ev.kind === 'overdue' ? 'agenda-item--overdue' : ''}`}
                        onClick={() => open(ev)}
                      >
                        <span class="agenda-item__time">{timeShort(ev.at)}</span>
                        <span class="agenda-item__label">
                          {ev.title}
                          {ev.sub ? ` · ${ev.sub}` : ''}
                        </span>
                      </button>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
